import { Alert, GetProp, Modal, Upload, UploadFile, UploadProps, Image } from "antd"
import { CSSProperties, FC, useEffect, useState } from "react"
import { UUID } from "../../generated/proto-ts/main"
import { InfoCircleFilled, PlusOutlined } from "@ant-design/icons"
import { COLOR_BG_BLUE, pbUUIDToUuid, uuidToPbUUID } from "../../utils/utils"
import ImageBlobReduce from "image-blob-reduce"
import Pica from "pica" // for image in-browser resizing before uploading

// DnD-kit for sortable list
import type { DragEndEvent } from "@dnd-kit/core"
import { DndContext, PointerSensor, useSensor } from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

import { FlexCol, FlexRow } from "./common"
import { Translated } from "../../utils/translated"

const pica = Pica({ features: ["js", "wasm", "cib"] })

interface DraggableUploadListItemProps {
  originNode: React.ReactElement<any, string | React.JSXElementConstructor<any>>
  file: UploadFile<any>
}

const DraggableUploadListItem = ({ originNode, file }: DraggableUploadListItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: file.uid,
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    cursor: "move",
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      // prevent preview event when drag end
      className={isDragging ? "is-dragging" : ""}
      {...attributes}
      {...listeners}
    >
      {/* hide error tooltip when dragging */}
      {file.status === "error" && isDragging ? originNode.props.children : originNode}
    </div>
  )
}

export const UploadImageListV2: FC<{
  pictures: UUID[]
  setPictures: React.Dispatch<React.SetStateAction<UUID[] | null>>
}> = ({ pictures, setPictures }) => {
  const [previewImageSrc, setPreviewImageSrc] = useState("")
  const [previewOpen, setPreviewOpen] = useState(false)

  const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false)
  const [removeImageUID, setRemoveImageUID] = useState<string>("")

  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    // console.log(
    //     `UploadImagesListV2: setting files with pictures`,
    //     pictures.map((e) => pbUUIDToUuid(e)),
    // )
    setFileList(
      pictures.map((pictureUUID) => {
        let uuidStr = pbUUIDToUuid(pictureUUID)
        return {
          uid: uuidStr,
          name: uuidStr,
          status: "done",
          url: `/api/uploads/${uuidStr}`,
        }
      }),
    )
  }, [pictures])

  const onPreview = async (file: UploadFile) => {
    setPreviewImageSrc(file.url || (file.preview as string))
    setPreviewOpen(true)
  }

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>
        <Translated keyEn="Upload" />
      </div>
    </button>
  )

  const maxCount = 5

  const sensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 10 },
  })

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setFileList((prev) => {
        const activeIndex = prev.findIndex((i) => i.uid === active.id)
        const overIndex = prev.findIndex((i) => i.uid === over?.id)
        return arrayMove(prev, activeIndex, overIndex)
      })
      setPictures((prev) => {
        if (prev === null) {
          return prev
        }
        const activeIndex = prev.findIndex((i) => pbUUIDToUuid(i) === active.id)
        const overIndex = prev.findIndex((i) => pbUUIDToUuid(i) === over?.id)
        return arrayMove(prev, activeIndex, overIndex)
      })
    }
  }

  return (
    <>
      <FlexCol>
        <Modal
          closable={false}
          open={removeConfirmationOpen}
          onCancel={() => setRemoveConfirmationOpen(false)}
          cancelText={<Translated keyEn="Cancel" />}
          okText={<Translated keyEn="Yes, remove" />}
          okButtonProps={{ danger: true }}
          onOk={() => {
            if (!removeImageUID) {
              setRemoveConfirmationOpen(false)
              return
            }
            fetch(`/api/uploads/${removeImageUID}`, {
              method: "DELETE",
            })
              .then((resp) => {
                if (!resp.ok) {
                  console.warn(
                    `Failed to delete file ${removeImageUID} on the server; will remove from the state anyway..`,
                  )
                }
              })
              .catch((e) => {
                console.warn(`Error deleting file ${removeImageUID} on the server:`, e)
              })
              .finally(() => {
                let _pictures: UUID[] = []
                for (let picture of pictures) {
                  let pictureUUID = pbUUIDToUuid(picture)
                  if (pictureUUID !== removeImageUID) {
                    _pictures.push(picture)
                  }
                }
                setPictures(_pictures)
                setRemoveConfirmationOpen(false)
                setRemoveImageUID("")
              })
          }}
        >
          <Alert
            type="error"
            showIcon
            closable={false}
            message={
              <>
                <Translated keyEn="Are you sure you want to remove this picture?" />
              </>
            }
          />
        </Modal>
        {previewImageSrc && (
          <Image
            src={previewImageSrc}
            hidden
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
            }}
          />
        )}
        <DndContext sensors={[sensor]} onDragEnd={onDragEnd}>
          <SortableContext
            items={fileList.map((i) => i.uid)}
            strategy={verticalListSortingStrategy}
          >
            <Upload
              accept="image/*,.pdf" // IMPORTANT: .pdf is a hack to make mobile devices offer camera capture AND file picker dialog
              capture="environment"
              action="/api/uploads/"
              listType="picture-card"
              multiple
              maxCount={maxCount}
              fileList={fileList}
              onPreview={onPreview}
              // rootClassName="upload-image-list-v2"
              className="upload-image-list-v2"
              itemRender={(originNode, file, fileList) => {
                let idx = fileList.findIndex((f) => f.uid === file.uid)
                let style: CSSProperties = {
                  width: "100%",
                  height: "100%",
                }
                if (idx === 0) {
                  style = {
                    ...style,
                    border: "2px solid #0065ff",
                    borderRadius: "12px",
                  }
                }
                return (
                  <div style={style}>
                    <DraggableUploadListItem originNode={originNode} file={file} />
                  </div>
                )
              }}
              beforeUpload={async (file) => {
                try {
                  let resizedBlob = await new ImageBlobReduce({
                    pica,
                  }).toBlob(file, {
                    max: 1024, // 768
                  })
                  return resizedBlob
                } catch (e: any) {
                  console.error(`Failed to resize image ${file.name}: ${e.message}`)
                  return file
                }
              }}
              onChange={({ fileList: newFilelist, file }) => {
                if (file.status === "done") {
                  let responseUUID = file.response as string
                  let pbUUID = uuidToPbUUID(responseUUID)
                  file.uid = responseUUID
                  file.url = `/api/uploads/${responseUUID}`
                  setPictures([...pictures, pbUUID])
                } else {
                  console.log(`UploadImageV2: newFileList`, newFilelist)
                  setFileList([...newFilelist])
                }
              }}
              onRemove={(file) => {
                setRemoveImageUID(file.uid)
                setRemoveConfirmationOpen(true)
                // actual removal from pictures state will be triggered in onOk of the confirmation modal, by setting fileList to the same list without the removed file, which triggers useEffect that syncs fileList and pictures
                return false // prevent default removal behavior, which is to immediately remove the file from the list; instead we want to wait for the confirmation modal, and only remove from the list (by setting fileList to a new list without the removed file) after the server deletion is attempted in onOk of the confirmation modal; this way we ensure that if the user cancels the removal, or if the server deletion fails, the picture will still be visible in the list
              }}
            >
              {pictures.length >= maxCount ? null : uploadButton}
            </Upload>
          </SortableContext>
        </DndContext>
        <Alert
          style={{
            marginBottom: 5,
          }}
          closable
          type="info"
          showIcon
          message={
            <>
              <b
                style={{
                  color: COLOR_BG_BLUE,
                }}
              >
                N.B.:{" "}
              </b>
              <Translated keyEn="only the" />{" "}
              <b
                style={{
                  color: COLOR_BG_BLUE,
                }}
              >
                <Translated keyEn="first" />
              </b>{" "}
              <Translated keyEn="picture will be added to the report. You can" />{" "}
              <b
                style={{
                  color: COLOR_BG_BLUE,
                }}
              >
                <Translated keyEn="drag-n-drop" />
              </b>{" "}
              <Translated keyEn="pictures to rearrange them as you prefer." />
            </>
          }
        />
      </FlexCol>
    </>
  )
}
